import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { del, get } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatCurrency, formatDate } from "@/lib/format";
import { ALL, LEAD_SOURCE_TYPES, LEAD_STATUSES, qs } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, PriorityPill } from "@/components/common/StatusPill";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ErrorState, ListCard, SearchInput, TableSkeleton } from "@/components/common/DataStates";
import { ConvertLeadDialog, LeadDialog } from "@/components/crm/dialogs";
import { FilterSelect, RowMenu, rowProps } from "@/pages/crm/shared";

const PAGE_SIZE = 25;

export const LeadsTab = ({ onOpenRecord, openNew, onNewHandled }) => {
  const { t } = useTranslation();
  const { orgId, hasRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasRole("member");
  const isAdmin = hasRole("admin");
  const [status, setStatus] = useState(ALL);
  const [sourceType, setSourceType] = useState(ALL);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const params = qs({ status, source_type: sourceType, q, page, page_size: PAGE_SIZE });
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: ["crm-leads", orgId, params], queryFn: () => get(`/crm/leads${params}`), enabled: !!orgId, placeholderData: (prev) => prev });
  const remove = useMutation({ mutationFn: (id) => del(`/crm/leads/${id}`), onSuccess: () => { toast.success(t("common.deleted")); setToDelete(null); qc.invalidateQueries({ queryKey: ["crm-leads"] }); }, onError: (e) => toast.error(errorMessage(e)) });
  const items = data?.items || [];
  const filtered = status !== ALL || sourceType !== ALL || q;

  return (
    <div className="space-y-4" data-testid="crm-leads-tab">
      <ListCard
        refreshing={isFetching && !isLoading}
        testId="crm-leads-card"
        toolbar={
          <>
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} className="w-full sm:w-64" testId="crm-leads-search" />
              <FilterSelect value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={LEAD_STATUSES} allLabel={t("crm.leads.allStatuses")} render={(s) => t(`status.${s}`)} counts={data?.counts} testId="crm-leads-filter-status" />
              <FilterSelect value={sourceType} onChange={(v) => { setSourceType(v); setPage(1); }} options={LEAD_SOURCE_TYPES} allLabel={t("crm.leads.allSources")} render={(s) => t(`crm.leadSourceType.${s}`, { defaultValue: s })} testId="crm-leads-filter-source" />
            </div>
            {canEdit && <Button size="sm" onClick={() => setEditing({})} data-testid="crm-leads-new"><Plus className="mr-2 h-4 w-4" />{t("crm.leads.new")}</Button>}
          </>
        }
        footer={data && <Pagination page={data.page} pageSize={data.page_size} total={data.total} onChange={setPage} testId="crm-leads-pagination" />}
      >
        {isError && <div className="p-4"><ErrorState message={errorMessage(error)} onRetry={refetch} testId="crm-leads-error" /></div>}
        {isLoading && <TableSkeleton testId="crm-leads-loading" />}
        {!isLoading && !isError && items.length === 0 && (
          <div className="p-4"><EmptyState icon={Users} title={filtered ? t("common.noResults") : t("crm.leads.empty")} hint={filtered ? undefined : t("crm.leads.emptyHint")} className="border-dashed shadow-none" testId="crm-leads-empty" action={!filtered && canEdit && <Button size="sm" onClick={() => setEditing({})} data-testid="crm-leads-empty-new"><Plus className="mr-2 h-4 w-4" />{t("crm.leads.new")}</Button>} /></div>
        )}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <Table data-testid="crm-leads-table">
              <TableHeader className="og-table-head"><TableRow><TableHead>{t("crm.leads.title_field")}</TableHead><TableHead>{t("crm.leads.status")}</TableHead><TableHead className="hidden md:table-cell">{t("crm.leads.source")}</TableHead><TableHead className="hidden text-right lg:table-cell">{t("crm.leads.value")}</TableHead><TableHead className="hidden lg:table-cell">{t("common.priority")}</TableHead><TableHead className="hidden md:table-cell">{t("crm.leads.createdAt")}</TableHead><TableHead className="w-10" /></TableRow></TableHeader>
              <TableBody>
                {items.map((lead) => (
                  <TableRow key={lead.id} {...rowProps(() => onOpenRecord("lead", lead.id), `crm-lead-row-${lead.id}`)}>
                    <TableCell className="max-w-[320px]">
                      <div className="truncate font-medium" data-testid={`crm-lead-title-${lead.id}`}>{lead.title}</div>
                      <div className="truncate text-xs text-muted-foreground">{[lead.contact_name, lead.company_name].filter(Boolean).join(" · ") || "—"}</div>
                    </TableCell>
                    <TableCell><StatusPill status={lead.status} testId={`crm-lead-status-${lead.id}`} /></TableCell>
                    <TableCell className="hidden text-xs md:table-cell">{t(`crm.leadSourceType.${lead.source_type}`, { defaultValue: lead.source_type })}{lead.lead_source_name ? <span className="text-muted-foreground"> · {lead.lead_source_name}</span> : null}</TableCell>
                    <TableCell className="hidden text-right font-mono text-xs lg:table-cell">{lead.estimated_value != null ? formatCurrency(lead.estimated_value, lead.currency) : "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell"><PriorityPill priority={lead.priority} /></TableCell>
                    <TableCell className="hidden whitespace-nowrap text-xs text-muted-foreground md:table-cell">{formatDate(lead.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <RowMenu testId={`crm-lead-menu-${lead.id}`} items={[
                        { key: "open", label: t("common.open"), onSelect: () => onOpenRecord("lead", lead.id) },
                        { key: "edit", label: t("common.edit"), onSelect: () => setEditing(lead), hidden: !canEdit },
                        { key: "convert", label: t("crm.leads.convert"), onSelect: () => setConverting(lead), hidden: !canEdit || lead.status === "converted" },
                        { key: "delete", label: t("common.delete"), onSelect: () => setToDelete(lead), destructive: true, separator: true, hidden: !isAdmin },
                      ]} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ListCard>

      <LeadDialog open={!!editing || !!openNew} onOpenChange={(o) => { if (!o) { setEditing(null); onNewHandled?.(); } }} initial={editing?.id ? editing : null} />
      <ConvertLeadDialog open={!!converting} onOpenChange={(o) => !o && setConverting(null)} lead={converting} onConverted={(d) => onOpenRecord("opportunity", d.opportunity.id)} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} description={t("common.confirmDelete")} destructive loading={remove.isPending} confirmLabel={t("common.delete")} onConfirm={() => remove.mutate(toDelete.id)} testId="crm-lead-delete-confirm" />
    </div>
  );
};
